import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  IconButton,
  Typography,
  Alert
} from '@mui/material';
import { X } from '@phosphor-icons/react';

function LegalDocumentModal({ open, onClose, fileName, title }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (open && fileName) {
      loadDocument();
    } else {
      // Reset state when modal closes
      setContent('');
      setError(null);
      if (contentRef.current) {
        contentRef.current.innerHTML = '';
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [open, fileName]);

  // Update ref when content changes - use direct DOM manipulation to ensure HTML renders
  useEffect(() => {
    // Use setTimeout to ensure DOM is ready after React renders
    const timer = setTimeout(() => {
      if (contentRef.current) {
        if (content) {
          // Direct DOM manipulation to ensure HTML is rendered, not escaped
          // This bypasses React's default text escaping
          contentRef.current.innerHTML = content;
        } else {
          contentRef.current.innerHTML = '';
        }
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, [content, open]); // Also trigger when modal opens

  const loadDocument = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Determine user type for filtering
      const userType = window.location.pathname.startsWith('/supplier') ? 'supplier' : 'user';
      
      // Use fetch directly to get raw HTML without axios parsing
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const url = `${apiBaseUrl}/api/legal/${fileName}?userType=${userType}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/html, text/plain, */*'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const htmlContent = await response.text();
      
      // Set content state
      setContent(htmlContent);
    } catch (err) {
      setError('Failed to load document. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{title || 'Legal Document'}</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            size="small"
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowY: 'auto', maxHeight: 'calc(90vh - 200px)' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {!loading && !error && (
          <Box
            sx={{
              '& h1': { mt: 2, mb: 1.5, fontSize: '2rem', fontWeight: 600 },
              '& h2': { mt: 2, mb: 1, fontSize: '1.5rem', fontWeight: 600 },
              '& h3': { mt: 1.5, mb: 0.5, fontSize: '1.25rem', fontWeight: 600 },
              '& h4': { mt: 1.5, mb: 0.5, fontSize: '1.1rem', fontWeight: 600 },
              '& h5': { mt: 1, mb: 0.5, fontSize: '1rem', fontWeight: 600 },
              '& h6': { mt: 1, mb: 0.5, fontSize: '0.9rem', fontWeight: 600 },
              '& p': { mb: 1.5, lineHeight: 1.7 },
              '& ul, & ol': { mb: 1.5, pl: 3 },
              '& li': { mb: 0.5 },
              '& strong': { fontWeight: 600 },
              '& em': { fontStyle: 'italic' },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 2,
              },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
                textAlign: 'left',
              },
              '& th': {
                bgcolor: 'grey.100',
                fontWeight: 600,
              },
              // Quill editor styles
              '& .ql-align-center': { textAlign: 'center' },
              '& .ql-align-right': { textAlign: 'right' },
              '& .ql-align-justify': { textAlign: 'justify' },
              '& .ql-size-small': { fontSize: '0.75em' },
              '& .ql-size-large': { fontSize: '1.5em' },
              '& .ql-size-huge': { fontSize: '2.5em' },
              '& img': { maxWidth: '100%', height: 'auto' },
            }}
          >
            <div 
              ref={contentRef}
              style={{ 
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                minHeight: '50px'
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LegalDocumentModal;
